/**
 * Agents.
 *
 * ## What this screen is careful about
 *
 * Four agents read the trader's own journal and describe what they find. The
 * whole surface is shaped by two constraints that are not negotiable:
 *
 * 1. **AEGIS is not an adviser** (`docs/02` Constraint 4). Everything here is
 *    descriptive. The server discards a run that produced directive language
 *    rather than showing it with a disclaimer, and when that happens this
 *    screen says so plainly instead of retrying quietly — a trader who asked
 *    for a review deserves to know the answer was thrown away.
 *
 * 2. **Nothing is applied for the trader.** The auto-tagger returns
 *    suggestions and the trader accepts them, individually or all at once. A
 *    tag they did not choose changes every report that groups by tag, and they
 *    would have no way to know why the numbers moved.
 *
 * ## No spinner theatre
 *
 * A run takes as long as it takes and there is no progress to report, so the
 * button says "Reading your session…" and stays disabled. A fake progress bar
 * would be inventing information about a process we cannot observe.
 */
import { useCallback, useEffect, useState } from 'react';
import type { JSX } from 'react';
import type { AgentDto, AgentId, AgentRunDto, AgentsResponse, Trade } from '@aegis/api-client';
import { ApiError, fmtTime } from '@aegis/api-client';
import { api } from '../lib/api.js';
import { EmptyState } from '../components/EmptyState.js';
import { Link } from '../lib/router.js';

const today = (): string => new Date().toISOString().slice(0, 10);

const when = (iso: string): string =>
  new Date(iso).toLocaleString(undefined, {
    month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
  });

/**
 * A refusal the trader can act on.
 *
 * The server distinguishes "you are not on a paid plan" from "this deployment
 * has no model" from "the answer was discarded for giving advice". Collapsing
 * them into "something went wrong" would leave a paying customer staring at an
 * error that upgrading cannot fix.
 */
function refusalText(e: unknown): string {
  // `ApiError.message` is already the server's sentence — the client lifts it
  // out of the response body rather than substituting a generic one, which is
  // exactly what makes it worth showing verbatim here.
  if (e instanceof ApiError) return e.message;
  return e instanceof Error ? e.message : 'The agent could not run.';
}

/**
 * A trade id is not a trade.
 *
 * `acct_user-demo__DEMO-LIVE:MES:MOCK-000069` tells the trader nothing about
 * which trade they are being asked to tag. The suggestion is unusable until it
 * says MES long, 20 July, 14:32 — so the screen loads the trades and labels
 * them, and falls back to the id only when it cannot find one.
 */
function labelFor(t: Trade | undefined, tradeId: string): string {
  if (!t) return tradeId;
  return `${t.symbol} ${t.direction} · ${fmtTime(t.openedAt)}`;
}

export function AgentsScreen(): JSX.Element {
  const [meta, setMeta] = useState<AgentsResponse | null>(null);
  const [runs, setRuns] = useState<AgentRunDto[]>([]);
  const [tradesById, setTradesById] = useState<Map<string, Trade>>(new Map());
  const [running, setRunning] = useState<AgentId | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [applied, setApplied] = useState<Record<string, number>>({});

  const load = useCallback(async () => {
    try {
      const [m, r] = await Promise.all([api.agents(), api.agentRuns()]);
      setMeta(m);
      setRuns(r.runs);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load the agents.');
      return;
    }
    // Separately and best-effort: the agents work without this, and a failure
    // here should degrade the suggestion labels rather than the whole screen.
    try {
      const { accounts } = await api.accounts();
      const map = new Map<string, Trade>();
      for (const a of accounts) {
        const { trades } = await api.trades(a.id);
        for (const t of trades) map.set(t.id, t);
      }
      setTradesById(map);
    } catch { /* labels fall back to the id */ }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const run = async (agentId: AgentId): Promise<void> => {
    setRunning(agentId);
    setError(null);
    try {
      const created = await api.runAgent(agentId, today());
      setRuns((rs) => [created, ...rs]);
    } catch (e) {
      setError(refusalText(e));
    } finally {
      setRunning(null);
    }
  };

  const applyAll = async (runId: string): Promise<void> => {
    try {
      const { applied: rows } = await api.applyAgentTags(runId);
      setApplied((a) => ({ ...a, [runId]: rows.length }));
    } catch (e) {
      setError(refusalText(e));
    }
  };

  const applyOne = async (runId: string, tradeId: string): Promise<void> => {
    try {
      const { applied: rows } = await api.applyAgentTags(runId, [tradeId]);
      setApplied((a) => ({ ...a, [runId]: (a[runId] ?? 0) + rows.length }));
    } catch (e) {
      setError(refusalText(e));
    }
  };

  if (!meta) {
    return error
      ? <div className="notice error">{error}</div>
      : <div className="dim">Loading…</div>;
  }

  return (
    <div className="scroll-area">
      <div className="section-head">
        <h2>Agents</h2>
        <span className="dim">
          They read your journal and describe it. They never tell you what to trade.
        </span>
      </div>

      {!meta.entitled && (
        <div className="notice">
          Agents are part of the paid plans. Everything they read — your journal,
          your strategies, your routine — stays available on every plan.
          {' '}<Link to="/pricing">See what changes</Link>.
        </div>
      )}

      {meta.entitled && !meta.available && (
        <div className="notice">
          This deployment has no model layer configured, so the agents cannot
          run. Every deterministic part of the journal is unaffected.
        </div>
      )}

      {error && <div className="notice error">{error}</div>}

      <div className="agent-grid">
        {meta.agents.map((a: AgentDto) => (
          <article key={a.id} className="agent-card panel">
            <header className="daily-card-head">
              <h3>{a.name}</h3>
              <span className="dim">
                {a.windowDays === 1 ? 'today' : `last ${a.windowDays} days`}
              </span>
            </header>
            <p className="panel-body">{a.description}</p>
            <button
              className="btn btn-primary compact"
              disabled={!meta.entitled || !meta.available || running !== null}
              onClick={() => void run(a.id)}
            >
              {running === a.id ? 'Reading…' : 'Run'}
            </button>
          </article>
        ))}
      </div>

      <section className="agent-runs">
        <h3>What they said</h3>
        {runs.length === 0 ? (
          <EmptyState icon="journal" title="No runs yet">
            Run an agent above and its answer is kept here. Re-reading a past run
            costs nothing — what it said on the day is a journal fact, and a fact
            that changes when you look at it again is not one.
          </EmptyState>
        ) : (
          <ul className="run-list">
            {runs.map((r) => {
              const meta2 = meta.agents.find((a) => a.id === r.agentId);
              const acceptedHere = applied[r.id];
              return (
                <li key={r.id} className="run">
                  <header>
                    <strong>{meta2?.name ?? r.agentId}</strong>
                    <span className="dim">{r.day} · {when(r.createdAt)}</span>
                  </header>
                  <p className="run-body">{r.body}</p>

                  {r.suggestions.length > 0 && (
                    <div className="run-suggestions">
                      <div className="run-suggestions-head">
                        <span className="dim">
                          {r.suggestions.length} suggested {r.suggestions.length === 1 ? 'tag set' : 'tag sets'}
                          {' — '}yours to accept, never applied for you
                        </span>
                        <button
                          className="btn btn-quiet compact"
                          onClick={() => void applyAll(r.id)}
                        >
                          Accept all
                        </button>
                      </div>
                      <ul className="suggestion-list">
                        {r.suggestions.map((s) => (
                          <li key={s.tradeId}>
                            <span className="suggestion-trade">
                              {labelFor(tradesById.get(s.tradeId), s.tradeId)}
                            </span>
                            {s.tags.map((t) => <span key={t} className="tag">{t}</span>)}
                            <button
                              className="btn btn-quiet compact"
                              onClick={() => void applyOne(r.id, s.tradeId)}
                            >
                              Accept
                            </button>
                          </li>
                        ))}
                      </ul>
                      {acceptedHere !== undefined && (
                        <p className="dim">
                          {acceptedHere} applied. They are ordinary tags now — edit or
                          remove them on the trade like any other.
                        </p>
                      )}
                    </div>
                  )}

                  {/* Which model wrote it, kept with the text. A reader a year
                      from now can tell what produced the sentence. */}
                  <footer className="dim mono">{r.modelId}</footer>
                </li>
              );
            })}
          </ul>
        )}
      </section>
    </div>
  );
}
